import React, {useContext, useEffect, useState} from "react";
import {Text, ScrollView, View, Image} from "react-native";
import MainStyle from "../../MainStyle";
import Button from "./Button";
import {Bet, Challenge, Payline} from "../../generated/smg_service_pb";
import {StoreContext} from "./StoreContextProvider";
import ContestFrame from "./ContestFrame";
import PayTable from "./PayTable";
import {isFullSwing} from "../constants";
import {useNavigation} from "@react-navigation/native";
import {colors} from "../assets/styles/colors";
import NewHeader from "./NewHeader";
import ChallengeInfoCard from "./ChallengeInfoCard";
import WinCircleIcon from "../assets/img/icons/Frame.svg";
import DistanceIcon from "../assets/img/icons/pin-distance-line 1.svg";
import TournamentRegistrationConfirmationModal from "../modals/TournamentRegistrationConfirmationModal";
import StartAutoPlayIcon from "../assets/img/icons/auto_play.svg";
import StopAutoPlayIcon from "../assets/img/icons/StopAutoPlayIcon.svg";
import ServiceClient from "../utils/ServiceClient";
import WagerCurrencySelector from "./WagerCurrencySelector";
import {useWagerCurrency} from "../hooks/useWagerCurrency";
import Div from "../lib/Div";
import P from "../lib/P";
import {footFormatter} from "../utils";
import ActiveFRSButton from "../screens/ActiveFRSButton";
import {track, trackBetPlaced} from "../services/analytics";

type Props = {
  bet: Bet, formik: any, title: string, type: 'open' | 'accepted', changeGameType?: () => void,
  moreGames?: boolean, showAutoPlayModal: (val?: 'StartAutoPlay') => void
};

const OpenBets = (props: Props) => {
  const {formik, title, type, bet} = props;
  const Context = useContext(StoreContext);
  const {cancelModalConfig, user, extendedUserData} = Context;
  // SMG-3446/3447: currency CATEGORY picker controller — drives the
  // selector render + the AcceptBet currency token.
  const wagerCurrency = useWagerCurrency();
  const [challengeId, setChallengeId] = useState<bigint>(BigInt(0));
  const navigation: any = useNavigation();
  const [confirmModalType, setConfirmModalType] = useState<'ChallengeFeeConfirmationModal' | 'ChallengeEntry' | 'ChallengeAmountModal' | ''>('');
  const AutoPlayIcon = (
    <Image
      source={(user?.autoPlayEnabled ? StopAutoPlayIcon : StartAutoPlayIcon) as any}
      style={[MainStyle.w_24, MainStyle.h_24]}
    />
  );

  let payLinesList: Payline[] | undefined;
  let challengesList: Challenge[] | undefined;
  let largestDistanceChallenge: Challenge | undefined;

  if (bet) {
    challengesList = bet.challenges;
    payLinesList = bet.paylines;
    largestDistanceChallenge = challengesList[0];

    challengesList.forEach(challenge => {
      if (largestDistanceChallenge && (largestDistanceChallenge.value2 < challenge.value2)) {
        largestDistanceChallenge = challenge;
      }
    });
  }

  const challengeFeeChange = (fee: string) => {
    setConfirmModalType('');
    formik.setFieldValue('contestAmount', fee);
    if (confirmModalType === 'ChallengeEntry') AcceptBet(fee);
  };

  const AcceptBet = (contestAmount: string, challengeId?: number | bigint) => {
    Context.setLoadingOverlay(true);
    const betAmount = contestAmount === '-2' ? extendedUserData.maxShotAmount : +contestAmount * 100;

    // SMG-3006: migrated from legacy ServiceClient.acceptBet(betId, ...)
    // which assumed engine pre-created a bet in 'open' state and we
    // flipped it via the smg-core path. Bay-session creates the bet
    // server-side on AcceptBet and returns the new betId; the polling
    // loop that used to surface the new bet is being retired in the
    // same PR. stationId is read from Context.stationId (Main.tsx now
    // mirrors the bay-session PlayerState.current_station_id into the
    // existing Context.stationId on every stream frame so this read is
    // up-to-date for the player's current bay).
    const stationId = BigInt(Context.stationId || 0);
    if (stationId === BigInt(0)) {
      console.warn('[OpenBets] AcceptBet: stationId unavailable from userSessionData; cannot dispatch via bay-session');
      Context.setLoadingOverlay(false);
      return;
    }
    // Resolve challengeId: explicit param wins, else fall back to
    // the bet's first challenge tier (the legacy challengeFeeChange
    // path didn't pass an explicit challengeId — bay-session
    // AcceptBet requires one, so we read from bet.challenges).
    let resolvedChallengeId = BigInt(0);
    if (challengeId !== undefined) {
      resolvedChallengeId = typeof challengeId === 'bigint' ? challengeId : BigInt(challengeId);
    } else if (challengesList && challengesList.length > 0 && challengesList[0].challengeId !== undefined) {
      resolvedChallengeId = BigInt(challengesList[0].challengeId);
    }
    if (resolvedChallengeId === BigInt(0)) {
      console.warn('[OpenBets] AcceptBet: challengeId is required for bay-session AcceptBet');
      Context.setLoadingOverlay(false);
      return;
    }

    // SMG-3447: currency from the category picker / server sticky
    // next_wager_currency instead of a hardcoded 'USD'. '' (Points) →
    // AcceptBet falls back to the server-resolved next_wager_currency.
    const acceptCurrency = wagerCurrency.resolvedCurrency ?? 'USD';
    ServiceClient.acceptBetV2(
      stationId, resolvedChallengeId, betAmount, acceptCurrency, Context.bayPlayerJwt
    ).then((result) => {
      // SMG-4111: the client emits Bet Placed for accepts THIS DEVICE
      // REQUESTED — this callback only fires for an acceptBetV2 the app made.
      // bay-session also accepts bets with no client callback at all
      // (auto-tee-up, wager-currency re-mint); those are reported by NEITHER
      // emitter and are tracked as SMG-4138. The engine emits separately for
      // the accepts IT performs.
      //
      // The two emitters are not deduped duplicates. See services/analytics.ts
      // for why insert_id cannot merge them (Amplitude scopes it to device_id,
      // which only client events have) and for the size of the gap.
      //
      // wasIdempotentReplay suppresses the case where this call accepted
      // nothing, which is the case the client previously could not detect.
      if (!result?.wasIdempotentReplay) {
        // Shared emitter — see services/analytics.ts for why the client and the
        // engine are disjoint populations rather than deduped duplicates.
        trackBetPlaced(result);
      }

      // 'Challenge Accepted' means "the player pressed accept", a real
      // client-side gesture. It is NOT a proxy for total accepts — use Bet
      // Placed for that.
      // NOTE: wagerAmount is the REQUESTED amount, not the booked one.
      // trackBetPlaced above deliberately reports the server's figure because
      // bay-session overrides it (PRACTICE forces 0). This event predates that
      // and still reports the request, so wagerAmount MUST NOT be summed as
      // handle — use Bet Placed's amount_cents for that.
      track('Challenge Accepted', {
        accept_source: 'manual',
        ...(challengeId !== undefined && {challengeId: Number(challengeId)}),
        ...(betAmount && {wagerAmount: betAmount}),
      });
      Context.getUser().then();

    }).catch(err => {
      console.log('acceptBet err', err);
      Context.setLoadingOverlay(false);

      if (err.message) {
        Context.setCancelModalConfig({
          visible: true, title: 'ERROR', subtitle: err.message, rightBtnText: 'OK',
          onResume: () => {
            Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          },
          onClose: () => {
            Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          }
        });
      }
    });
  };

  const CancelBet = (betId: number | bigint) => {
    Context.setLoadingOverlay(true);

    ServiceClient.refundBet(betId).then(resolved => {
      if (resolved) {
        setTimeout(() => {
          Context.getUser().then();
        }, 1500);
      }

    }).catch(err => {
      console.log('cancelBet err', err);
      Context.setLoadingOverlay(false);

      if (err.message) {
        Context.setCancelModalConfig({
          visible: true, title: 'Error', leftBtnText: 'OK',
          subtitle: err.code === 20020043 ? 'Challenge in progress. Unable to cancel challenge.' : err.message,
          onExit: () => {
            Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          }
        });
      }
    });
  };

  const handleChallengeDeclined = (currentChallengeId?: number | bigint) => {
    track('Challenge Declined', {
      ...(currentChallengeId !== undefined && {challengeId: Number(currentChallengeId)}),
    });
  };

  const onEnterContestModalOpen = () => {
    if (type === 'open') {
      if (bet.challenges.length) {
        setConfirmModalType('ChallengeEntry');
      }

    } else if (type === 'accepted') {
      CancelBet(bet.betId);
    }
  };

  useEffect(() => {
    if (bet.challenges.length) setChallengeId(bet.challenges[0].challengeId);
  }, [bet.betType, bet.betState]);

  return (
    <ScrollView contentContainerStyle={MainStyle.flex1} scrollEventThrottle={16}>
      <NewHeader/>

      <Div w={333} mx="auto">
        <P fw="500" fs={30} lh={24} mb={12}>{title}</P>

        <ContestFrame
          betType={bet.betType} type={type} onArrowForwardPress={() => {}}
          largestDistanceChallenge={largestDistanceChallenge}
        />

        <Div row justifyContent="space-between" mb={10} alignItems="flex-start">
          <View>
            <>
              <ChallengeInfoCard
                title="Distance"
                value={`${largestDistanceChallenge ? Number(largestDistanceChallenge.value1) / 36 : 0} Yards`}
                icon={DistanceIcon as any} mb={8}
              />

              <ChallengeInfoCard
                title="Win Circle"
                value={footFormatter.format(largestDistanceChallenge ? Math.round(Number(largestDistanceChallenge.value2) / 12) : 0)}
                icon={WinCircleIcon as any} mb={8}
              />

              {type === 'accepted' ? (
                <ChallengeInfoCard
                  leftText="Challenge Amount" changeFee={() => props.showAutoPlayModal('StartAutoPlay')}
                  formik={formik} maxShotAmount={extendedUserData.maxShotAmount}
                  autoPlayAmount={user?.autoPlayAmount} autoPlayEnabled={user?.autoPlayEnabled}
                />
              ) : (
                <ChallengeInfoCard
                  leftText="Challenge Amount" changeFee={() => setConfirmModalType('ChallengeAmountModal')}
                  formik={formik} maxShotAmount={extendedUserData.maxShotAmount}
                  autoPlayAmount={user?.autoPlayAmount} autoPlayEnabled={user?.autoPlayEnabled}
                />
              )}
            </>
          </View>

          <PayTable
            payLinesList={payLinesList} challengesList={challengesList}
            amount={type === 'open' ? (formik.values.contestAmount === '-2' ? extendedUserData.maxShotAmount : +formik.values.contestAmount * 100) : Number(bet.amount)}
            holeInOneAmount={bet.holeInOneBonusAmount}
          />
        </Div>

        {/* SMG-3446: Real Money vs Points category picker — renders ONLY when
            the station offers ≥2 wager categories (single-availability rule). */}
        <WagerCurrencySelector/>

        <Div row mb={10}>
          {user !== undefined && (
            <Div flex={1}>
              <Button
                text={user.autoPlayEnabled ? `Stop\nAutoPlay` : `Start\nAutoPlay`}
                onPress={() => props.showAutoPlayModal()}
                bg={user.autoPlayEnabled ? colors.stop_auto_play : colors.newGreenText} autoPlayIcon={AutoPlayIcon}
                color={user.autoPlayEnabled ? colors.newGreenText : 'black'} w="100%" fontSize={20}
                fontWeight="600"
                disabled={extendedUserData.isNewPlayer || extendedUserData.freeShotNumber !== extendedUserData.freeShotCount}
              />
            </Div>
          )}

          <Div w={9}/>

          <Div flex={1}>
            <Button
              text={type === 'open' ? `Enter\nChallenge` : `Cancel\nChallenge`} onPress={onEnterContestModalOpen}
              bg="transparent" borderWidth={1} borderColor={colors.white} color="white" w="100%" fontSize={20}
              disabled={extendedUserData.isNewPlayer || extendedUserData.freeShotNumber !== extendedUserData.freeShotCount}
              fontWeight="600"
            />
          </Div>
        </Div>

        {isFullSwing && <ActiveFRSButton/>}

        {extendedUserData.isNewPlayer || extendedUserData.freeShotNumber !== extendedUserData.freeShotCount ? (
          <Text style={styles.terms}>
            * See <Text style={MainStyle.c_new_green_text}
                        onPress={() => navigation.navigate('TermsAndConditions')}>Terms & Conditions</Text>
          </Text>
        ) : (
          <P color={colors.gray} ta="center" fw="500">Malfunction voids all pays and plays</P>
        )}
      </Div>

      {!!formik.values.contestAmount && (
        <TournamentRegistrationConfirmationModal
          isOpen={!!confirmModalType}
          onClose={() => {
            if (confirmModalType === 'ChallengeEntry') handleChallengeDeclined(challengeId);
            setConfirmModalType('');
          }}
          joinTournament={challengeFeeChange}
          type={confirmModalType} entryFee={0} entryChallengeFee={formik.values.contestAmount}
          min_h={confirmModalType === 'ChallengeEntry' ? 372 : undefined}
        />
      )}
    </ScrollView>
  );
};

const styles = {
  terms: [MainStyle.c_white, MainStyle.t_a_c, MainStyle.Barlow500, MainStyle.f_s_16]
};

export default React.memo(OpenBets);
